<template>
  <div
    class="cld-glass relative h-full bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm transition-all duration-200 hover:shadow-lg hover:border-[#008556]"
    :class="[gridType === 'list' ? '' : 'min-h-[450px] w-full']"
    :id="`Product-box-items-${item.Identifier}`"
    :key="`Product-box-items-${item.Identifier}`"
  >
    
    <!-- Preorder Label -->
    <div class="absolute z-10 -left-1.5 -top-1 flex flex-col" v-if="item?.statusPreorder">
      <span class="bg-primary text-white text-[11px] font-medium tracking-tight text-center px-2 py-1 rounded-t">
        {{ $t("release_date") }}
      </span>
      <span class="text-primary bg-white border border-primary text-[11px] font-medium tracking-tight text-center px-2 py-1 rounded-b">
        {{ formatDate(item.releaseDate) }}
      </span>
    </div>
    
    <!-- Promo Label -->
    <div class="absolute z-10 -left-1.5 -top-1 flex flex-col" v-if="item?.isPromo">
      <span class="bg-red-600 text-white text-[11px] font-medium tracking-tight text-center px-2 py-1 rounded-t">
        {{ $t("promo") }}
      </span>
      <span class="text-red-600 bg-white border border-red-600 text-[11px] font-medium tracking-tight text-center px-2 py-1 rounded-b">
        {{ "Hurry up!" }}
      </span>
    </div>

    <!-- Language Flags -->
    <div class="absolute z-10 right-1 top-11 flex flex-col gap-1" v-if="item.vgMenuLanguages.length">
      <div v-for="lng in item.vgMenuLanguages">
        <img
          height="20"
          class="w-5 h-auto rounded-sm"
          :src="getLangImgUrl(lng)"
          :id="item.Identifier"
          :alt="lng"
          :key="lng"
          onerror="this.style.display='none'"
        />
      </div>
    </div>

    <div :class="gridType === 'list' ? 'flex' : 'flex flex-col h-full'">
      <!-- Image Wrapper -->
      <div
        :class="gridType === 'list'
          ? 'w-[400px] h-[280px] flex justify-center items-center bg-gray-50 group'
          : 'relative bg-gray-50 p-2 flex items-center justify-center min-h-[230px] group'"
      >
        <div class="front">
          <NuxtLinkLocale :to="{
            path:
              '/products/' +
              item.Identifier +
              '/' +
              productLinkTitleString(item.computed_attr),
          }" aria-label="Image-Link">
            <img v-if="item.images[0] || (item.imagesDark && item.imagesDark[0])" :src="mainImageSrc" :id="item.Identifier"
              class="object-contain max-w-full max-h-[230px] transition-transform duration-200 group-hover:scale-[1.02]" :class="gridType === 'list' ? 'h-full w-full' : ''" :alt="item.computed_attr" key="1img"
              @error="handleImageError" :loading="index < 6 ? 'eager' : 'lazy'" width="200"
              height="180" :fetchpriority="index < 3 ? 'high' : 'auto'" decoding="async" />

            <!-- "Coming soon" placeholder: theme-picked via the coming-soon-* rules
                 in dark.css (both rendered; CSS shows one — no hydration mismatch). -->
            <template v-else>
              <img
                src="https://assets.cld.be/cdn-cgi/image/width=200,quality=40/9/3/1/0/931054989870582dda8788cf81759668c311beee_Image_Coming_Soon.png"
                :id="item.Identifier" class="object-contain max-w-full max-h-[230px] coming-soon-light" :alt="item.computed_attr"
                :loading="index < 6 ? 'eager' : 'lazy'" width="200" height="180" decoding="async" />
              <img
                src="/image-coming-soon-dark.jpg"
                :id="item.Identifier" class="object-contain max-w-full max-h-[230px] coming-soon-dark" :alt="item.computed_attr"
                loading="lazy" width="200" height="180" decoding="async" />
            </template>
          </NuxtLinkLocale>
        </div>

        <!-- Quick View Button -->
        <div class="absolute bottom-0 left-0 right-0 flex justify-center pb-3 opacity-0 group-hover:opacity-100 transition-opacity print:hidden">
          <button :title="$t('cart.quick_view')" @click="showQuickModal(item)" 
            class="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center hover:bg-primary hover:text-green-500 transition-colors">
            <i class="fa fa-search" aria-hidden="true"></i>
          </button>
        </div>
      </div>
      
      <div class="min-w-0 flex-1 flex flex-col">
        <!-- ID & EAN (Selectable, Non-clickable) -->
        <div class="flex flex-wrap gap-2 text-xs mb-1 px-5 pt-4 select-text">
          <span class="text-gray-900 font-medium">ID: {{ item.Identifier }}</span>
          <span class="text-gray-900 font-medium">EAN: {{ item.ean }}</span>
        </div>

       <NuxtLinkLocale :to="{
          path:
            '/products/' +
            item.Identifier +
            '/' +
            productLinkTitleString(item.computed_attr),
        }" aria-label="Product-Link">
          <div :class="gridType === 'list' ? 'w-[534px] min-h-[218px] pt-12' : ''"
            class="px-5 py-4 text-[13px] leading-[18px] text-gray-600 flex-1">
            <ClientOnly>
              <h6
                class="text-[13px] font-semibold text-gray-900 mb-2 line-clamp-2 min-h-[36px] group relative cursor-help">
                <div
                  class="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 w-64 text-center pointer-events-none font-normal">
                  {{ item.name }}
                  <div class="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
                </div>
                {{ item.name }}
              </h6>
            </ClientOnly>

            <!-- Brand -->
            <div class="text-xs mb-1">{{ $t('brand') }}: {{ item.brandText }}</div>

            <!-- Stock -->
            <div class="text-xs mb-1 min-h-[20px]">
              <span v-if="!item.statusPreorder">
                {{ $t('stock') }}:
                <span v-if="item.stock >= 30" class="inline-flex items-center justify-center ml-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#009d66] text-white">30+</span>
                <span v-else-if="item.stock > 0" class="inline-flex items-center justify-center ml-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#009d66] text-white">{{ item.stock }}</span>
                <span v-else class="inline-flex items-center justify-center ml-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-400 text-white">{{ item.stock }}</span>
              </span>
            </div>

            <!-- Price -->
            <div class="text-sm font-semibold mb-1 flex items-center gap-2 min-h-[22px]">
              <template v-if="item.price">
                <span class="text-[#009d66] whitespace-nowrap">
                  {{ $t('price') }}: € {{ showDecimalValueInLocale(item.price) }}
                </span>
              </template>
            </div>

            <!-- RefCli on its own row: customer refs are long (16+ digits) and
                 squeezed the price onto two lines when shown inline. -->
            <div v-if="item.refCli" class="mb-1 min-w-0">
              <span :title="`RefCli: ${item.refCli}`"
                class="block max-w-full truncate px-2 py-0.5 rounded text-[11px] font-medium bg-primary text-white">
                RefCli: {{ item.refCli }}
              </span>
            </div>


            <!-- Retail Price + BO -->
            <div class="flex items-center justify-between text-xs mb-1 min-h-[20px]">
              <span v-if="item.dvdSuggestedPriceAmount" class="text-[#009d66]">
                {{ $t('products.retail_price') }}:
                € {{ showDecimalValueInLocale(item.dvdSuggestedPriceAmount) }}
              </span>

              <span v-if="bo" class="px-2 py-0.5 rounded text-[11px] font-medium bg-red-600 text-white mr-3">
                BO: {{ bo }}
              </span>
            </div>
          </div>
        </NuxtLinkLocale>
        
        <client-only>
          <!-- Render the (fixed, full-screen) quick-view only from the clicked card.
               showQuickView is global store state, so gating on `.show` alone made
               every mounted card render a duplicate modal. -->
          <OnClickOutside @trigger="close" v-if="showQuickView.show && showQuickView.item?.Identifier === item.Identifier">
            <WidgetsQuickview :productData="showQuickView.item" :isAuthenticated="isAuthenticated" />
          </OnClickOutside>
        </client-only>
        
        <!-- Add to Cart Section -->
        <WidgetsLoader :loading="pending">
          <div class="mt-auto flex items-center gap-2 px-5 pb-4 pt-3 border-t border-gray-100 print:hidden" v-if="isAuthenticated && !item.readOnly">
            <input type="number" v-model="inputval" min="1" max="10000"
              class="w-[64px] h-9 text-center border border-gray-300 rounded-lg focus:border-[#009d66] focus:ring-2 focus:ring-[#4CAF50]/30 outline-none">
            <button @click="addToCart(inputval)" 
              class="flex-1 h-9 bg-[#009d66] text-white rounded-lg hover:bg-[#008556] transition-colors flex items-center justify-center gap-2 font-semibold">
              <i class="fa fa-shopping-cart"></i>
              <span v-if="gridType !== 'medium'" class="text-sm">{{ $t('add_to_cart') }}</span>
            </button>
          </div>
          <div class="mt-auto flex items-center gap-2 px-5 pb-4 pt-3 border-t border-gray-100 print:hidden" v-else-if="isAuthenticated && item.readOnly">
            <button class="w-full h-9 bg-[#009d66] hover:bg-[#008556] text-white rounded-lg flex items-center justify-center">
              <i class="fa fa-eye"></i>
            </button>
          </div>
        </WidgetsLoader>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useUserStore } from "~/store/UserStore";
import { useCartStore } from "~/store/CartStore";
import { useUtilityStore } from "~/store/UtililityStore";
import { trackAddToCart, mapProduct } from "~/composables/gtmEvents";
import { useFilterStore } from "~/store/FilterStore";
import { OnClickOutside } from "@vueuse/components/index";
import { useI18n } from "vue-i18n";
const userStore = useUserStore();
const cartStore = useCartStore();
const utilityStore = useUtilityStore();
const filterStore = useFilterStore();

const cldUser = computed(() => userStore.cldUser);
// const selectedLanguage = computed(() => utilityStore.selectedLanguage);
// const selectedLocale = computed(() => utilityStore.selectedLocale);
const isAuthenticated = computed(() => userStore.isAuthenticated);
const showQuickView = computed(() => filterStore.showQuickView)
const props = defineProps(["index", "item", "bo"]);
// const imageSrc = ref("");
const { t } = useI18n();
// const pageReady = ref(false);
const pending = ref(false);
// const emit = defineEmits();
const close = () => {
  filterStore.setShowQuickView({ show: false, item: {} })
};
const { showDecimalValueInLocale, productLinkTitleString, productTitle } = useUtils();
// Methods
// const showDecimalValueInLocale = computed(() => {
//   return (v) => {
//     if (v) {
//       try {
//         return Number(v).toFixed(2).toLocaleString(selectedLocale);
//       } catch (error) {
//         return v;
//       }
//     }
//     return "";
//   };
// });



// Packshots are 2:3 portraits (boxed image is returned first by the BE), so we
// request a 2:3 box; the theme-aware pad background keeps any bars invisible.
const { getImgUrl: buildImgUrl } = useProductImage();
const getImgUrl = (path) => buildImgUrl(path, { width: 200, height: 300 });

// Server-generated transparent packshot (PIM attribute
// product_image_boxed_large_generated, exposed by products/search/text as
// `imagesDark`): preferred when present — its white background is already
// removed so the card blends into the theme's image well. Products without it
// (not yet processed, or skipped as borderless artwork) keep the original.
// The CDN webp conversion preserves the PNG's transparency. NO `fit=pad` here:
// the CDN can pad with WHITE bars, which would reintroduce the exact white
// rectangle the generated image removed — `scale-down` adds no padding and the
// card's object-contain CSS handles the 2:3 box.
const mainImageSrc = computed(() =>
  props.item.imagesDark?.[0]
    ? buildImgUrl(props.item.imagesDark[0], { width: 200, height: 300, fit: "scale-down" })
    : props.item.images?.[0]
      ? getImgUrl(props.item.images[0])
      : ""
);

// const imgSource = computed(() => languageMap[language.value]);

const getLangImgUrl = (path) =>
  languageMap[path.toLocaleLowerCase()] ||
  `https://unpkg.com/language-icons/icons/${path.toLocaleLowerCase()}.svg`;

const gtmAddToCart = () => {
  const mappedItem = mapProduct([props.item]);

  trackAddToCart({
    items: mappedItem,
    customer_id: cldUser.value?.id,
    value: props.item["dvdSuggestedPriceAmount"],
  });
};


let inputval = ref(1)



const addToCart = async (inputQty) => {
  pending.value = true;
  try {
    const { status } = await cartStore.addToCart({
      sku: props.item.Identifier,
      qty: inputQty,
    });
    if (status) {
      useNuxtApp().$showToast({
        msg: t('alerts.added_to_cart.desc'),
        type: "success",
      });
      gtmAddToCart();
    } else {
      useNuxtApp().$showToast({
        msg: t('alerts.failed_add_cart.desc'),
        type: "error",
      });
    }
  } finally {
    pending.value = false;
  }
};

const showQuickModal = (item) => {
  filterStore.setShowQuickView({ show: true, item })
};

const formatDate = computed(() => (date) => {
  const d = new Date(date);
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
});
// const imgChange = (imgsrc) => {
//   imageSrc.value = imgsrc;
// };

// onNuxtReady(() => {

// });

// grid type control through utility store
const gridType = computed(() => utilityStore.gridListType)


const handleImageError = (event) => {
  const fallbackUrl = 'https://assets.cld.be/cdn-cgi/image/width=200,quality=40/9/3/1/0/931054989870582dda8788cf81759668c311beee_Image_Coming_Soon.png';
  const secondImage = props.item.images[1];
  const imgElement = event.target;

  // A broken dark packshot falls back to the original image first.
  if (props.item.imagesDark?.[0] && props.item.images?.[0] && !imgElement.dataset.triedOriginal) {
    imgElement.dataset.triedOriginal = 'true';
    imgElement.src = getImgUrl(props.item.images[0]);
    return;
  }

  if (secondImage && !imgElement.dataset.triedSecond) {
    // Try the second image if available
    imgElement.dataset.triedSecond = 'true';
    imgElement.src = getImgUrl(secondImage);
  } else {
    // Fallback to the "coming soon" placeholder matching the active theme
    // (this runs client-side only, so reading the html.dark class is safe).
    imgElement.src = document.documentElement.classList.contains('dark')
      ? '/image-coming-soon-dark.jpg'
      : fallbackUrl;
  }
}
</script>

